import { isFlagged, RISK } from "@/lib/skills/constants";
import type { Category, RiskLevel } from "@/lib/skills/types";

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** Wire shape sent to the client (no timestamp — the table never shows it). */
export interface ModerationRow {
  id: string;
  slug: string;
  title: string;
  cat: Category[];
  risk: RiskLevel;
  version: string;
  downloads: number;
  published: boolean;
  ownerName: string;
  openFindings: number;
}

/** Server-internal shape carrying createdAt for the new-this-week stat. */
export interface ModerationRowInternal extends ModerationRow {
  createdAt: Date;
}

export type ModerationTab = "all" | "flagged" | "unpublished";

export interface ModerationStats {
  published: number;
  unpublished: number;
  flagged: number;
  openFindings: number;
  skillsWithFindings: number;
  newThisWeek: number;
}

export function filterModerationRows<T extends ModerationRow>(rows: T[], tab: ModerationTab): T[] {
  if (tab === "flagged") return rows.filter((r) => isFlagged(r.risk));
  if (tab === "unpublished") return rows.filter((r) => !r.published);
  return rows;
}

/** Highest risk first (non-mutating). */
export function sortRowsByRisk<T extends ModerationRow>(rows: T[]): T[] {
  return [...rows].sort((a, b) => RISK[b.risk].rank - RISK[a.risk].rank);
}

/** `now` injected for deterministic tests. */
export function computeModerationStats(rows: ModerationRowInternal[], now: Date): ModerationStats {
  const nowMs = now.getTime();
  return {
    published: rows.filter((r) => r.published).length,
    unpublished: rows.filter((r) => !r.published).length,
    flagged: rows.filter((r) => isFlagged(r.risk)).length,
    openFindings: rows.reduce((a, r) => a + r.openFindings, 0),
    skillsWithFindings: rows.filter((r) => r.openFindings > 0).length,
    newThisWeek: rows.filter((r) => nowMs - r.createdAt.getTime() <= WEEK_MS).length,
  };
}
